/**
 * Abrir un correo NUEVO ya escrito en el servicio del usuario — botón «Enviar»
 * de presupuestos y facturas (2026-10-22).
 *
 * El correo lo manda el usuario desde SU cuenta: Faktelio no envía nada, así
 * que no tiene coste por usuario ni consume «Emails documentos».
 *
 * ```text
 * gmail    mail.google.com, redactar (view=cm)
 * outlook  outlook.live.com: Outlook.com y Hotmail
 * local    mailto: el programa de correo del ordenador o del móvil
 * WhatsApp wa.me, el chat con el teléfono de la ficha (al final del fichero)
 * ```
 *
 * Ninguno de los tres puede llevar el PDF adjunto: no hay forma de adjuntar
 * un fichero desde un enlace. Por eso la pantalla lo DESCARGA a la vez y dice
 * que hay que adjuntarlo. Donde el navegador sabe compartir ficheros (móvil,
 * y Chrome, Edge o Safari en el ordenador), el PDF sí va adjunto: eso es
 * `navigator.share`, no esto.
 *
 * Todo lo que escribe el usuario va codificado con `encodeURIComponent`: un
 * `?`, un `&` o un salto de línea en el asunto no pueden abrir otro campo ni
 * añadir destinatarios.
 */

export type ServicioCorreo = "gmail" | "outlook" | "local";

export type Borrador = {
  readonly para: string;
  readonly asunto: string;
  readonly cuerpo: string;
};

const c = encodeURIComponent;

/**
 * Sólo una dirección con forma de dirección, y en ASCII, viaja como
 * destinatario. Lo demás —vacío, dos direcciones, algo con `?`, `%` o
 * espacios— deja el «Para» en blanco: mejor que lo escriba el usuario que
 * abrir un correo a quien no era, o con un campo que no escribió nadie.
 */
export function destinatarioValido(para: string): string {
  const limpio = para.trim();
  return /^[A-Za-z0-9._+'-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+$/.test(limpio) ? limpio : "";
}

export function enlaceRedactar(servicio: ServicioCorreo, b: Borrador): string {
  const para = destinatarioValido(b.para);
  if (servicio === "gmail") {
    return `https://mail.google.com/mail/?view=cm&fs=1&to=${c(para)}&su=${c(b.asunto)}&body=${c(b.cuerpo)}`;
  }
  if (servicio === "outlook") {
    return `https://outlook.live.com/mail/0/deeplink/compose?to=${c(para)}&subject=${c(b.asunto)}&body=${c(b.cuerpo)}`;
  }
  // RFC 6068: los saltos de línea del cuerpo van como %0D%0A. La dirección va
  // tal cual: ya se comprobó que no lleva nada que codificar, y hay programas
  // de correo que no descodifican un "%40".
  return `mailto:${para}?subject=${c(b.asunto)}&body=${c(b.cuerpo.replace(/\r?\n/g, "\r\n"))}`;
}

/**
 * El número de WhatsApp de la ficha, como lo pide `wa.me`: sólo cifras, con
 * el prefijo del país delante y sin `+` ni `00` (2026-10-23).
 *
 * ```text
 * +34 600 12 34 56 / 0034...   el prefijo que lleve, tal cual
 * 600 123 456 de un cliente ES  se le pone el 34: un móvil o fijo español
 *                               de 9 cifras que empieza por 6, 7, 8 o 9
 * lo demás                      "" y WhatsApp deja elegir el contacto: un
 *                               número sin prefijo de otro país no se adivina
 * ```
 */
export function telefonoWhatsApp(telefono: string | null, pais: string): string {
  if (telefono === null) return "";
  const t = telefono.trim().replace(/[ ()-]/g, "");
  const internacional = t.match(/^(?:\+|00)([1-9][0-9]{7,14})$/);
  if (internacional !== null) return internacional[1] ?? "";
  if (pais === "ES" && /^[6789][0-9]{8}$/.test(t)) return `34${t}`;
  return "";
}

/**
 * El chat de WhatsApp con el mensaje escrito. Como los enlaces de correo, NO
 * puede llevar el PDF: la pantalla lo descarga a la vez. Sin número, `wa.me`
 * abre el selector de contactos.
 */
export function enlaceWhatsApp(numero: string, texto: string): string {
  return `https://wa.me/${/^[0-9]+$/.test(numero) ? numero : ""}?text=${c(texto)}`;
}
